import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
// next
import { useRouter } from 'next/router';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Stack, Drawer, List, ListSubheader, IconButton } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import useCompany from '../../../hooks/useCompany';
// config
import { NAVBAR } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Scrollbar from '../../../components/Scrollbar';
import SvgIconStyle from '../../../components/SvgIconStyle';
import { NavSectionVertical } from '../../../components/nav-section';
import { ListItemStyle, ListItemIconStyle, ListItemTextStyle } from '../../../components/nav-section/vertical/style';
import navConfig from './NavConfig';
import NavbarCompany from './NavbarCompany';
import { EmbedOnboardingDialog } from '../../../sections/onboarding';
// routes
import { PATH_DASHBOARD, PATH_JOBS, PATH_TIME, PATH_SETTINGS } from '../../../routes/paths';

// ----------------------------------------------------------------------

export const ListSubheaderStyle = styled((props) => <ListSubheader disableSticky disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.overline,
    color: theme.palette.text.primary,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shorter,
    }),
  })
);

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const { crews, activeCrews } = useCompany();
  const [isOnboardingDialogOpen, setIsOnboardingDialogOpen] = useState(false);
  const router = useRouter();
  const { pathname } = router;

  const isDesktop = useResponsive('up', 'lg');
  const smUp = useResponsive('up', 'sm');

  const generateCrewMenuData = (crews, activeCrews) => {
    if (!crews) return [];
    if (crews.length === 0) return [];

    const header = {
      title: '계약 중인 크루',
      path: PATH_JOBS.root,
    }
    const items = activeCrews.map((crew) => {
      if (crew.use_salary_claim) {
        return {
          id: crew.id,
          randomhash: crew.randomhash,
          nickname: crew.nickname,
          path: `${PATH_DASHBOARD.root}?c=${crew.id}`,
        }
      }
      else {
        return {
          id: crew.id,
          randomhash: crew.randomhash,
          nickname: crew.nickname,
          path: `${PATH_TIME.root}?c=${crew.id}`,
        }
      }
    });

    return [
      {
        header,
        items,
      }
    ]
  };

  const crewData = generateCrewMenuData(crews, activeCrews);

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const goSetting = (e) => {
    e.preventDefault();
    router.push({pathname: PATH_SETTINGS.root, query: router.query});
  };

  const mobileRenderContent = (
    <Scrollbar
      sx={{
        height: '100vh',
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Logo />
        </Stack>

        <NavbarCompany />
      </Stack>

      <NavSectionVertical crews={crewData} navConfig={navConfig} />

      <Box sx={{ px: 2, pt: 4, pb: { xs: 0, sm: 3 } }}>
        <List disablePadding>
          <ListItemStyle onClick={goSetting}>
            <ListItemIconStyle>
              <SvgIconStyle src={`/icons/icon-setting-mono.svg`} />
            </ListItemIconStyle>
            <ListItemTextStyle disableTypography primary={'환경 설정'} />
          </ListItemStyle>
          <ListItemStyle onClick={() => setIsOnboardingDialogOpen(true)}>
            <ListItemIconStyle>
              <SvgIconStyle src={`/icons/ic_check.svg`} sx={{ width: 1, height: 1 }} />
            </ListItemIconStyle>
            <ListItemTextStyle disableTypography primary={'이용 가이드'} />
          </ListItemStyle>
        </List>
      </Box>

    </Scrollbar>
  )

  const desktopRenderContent = (
    <>
    <Scrollbar
      sx={{
        height: smUp ? 'calc(100vh - 190px)' : 'calc(100vh - 160px)',
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Logo />
        </Stack>

        <NavbarCompany />
      </Stack>

      <NavSectionVertical crews={crewData} navConfig={navConfig} />

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>

    <Box sx={{ position: 'fixed', bottom: 0, width: 'inherit', px: 2, pb: { xs: 0, sm: 3 } }}>
      <List disablePadding>
        <ListItemStyle onClick={goSetting}
          sx={{ ...pathname === "/settings" && { fontWeight: "bold", backgroundColor: "grey.200" } }}
        >
          <ListItemIconStyle>
            <SvgIconStyle src={`/icons/icon-setting-mono.svg`} />
          </ListItemIconStyle>
          <ListItemTextStyle disableTypography primary={'환경 설정'} />
        </ListItemStyle>
        <ListItemStyle onClick={() => setIsOnboardingDialogOpen(true)}>
          <ListItemIconStyle>
            <SvgIconStyle src={`/icons/ic_check.svg`} sx={{ width: 1, height: 1 }} />
          </ListItemIconStyle>
          <ListItemTextStyle disableTypography primary={'이용 가이드'} />
        </ListItemStyle>

      </List>
    </Box>
    </>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: NAVBAR.DASHBOARD_WIDTH,
        },
      }}
    >
      {!isDesktop && (
        <Drawer open={isOpenSidebar} onClose={onCloseSidebar} PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}>
          {mobileRenderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: 'dashed',
              bgcolor: 'background.default',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
            },
          }}
        >
          {desktopRenderContent}
        </Drawer>
      )}
      <EmbedOnboardingDialog
        isOpen={isOnboardingDialogOpen}
        closeDialog={() => setIsOnboardingDialogOpen(false)}
      />
    </RootStyle>
  );
}
